import React, { useState } from 'react';
import styles from './PatientHistory.module.css';
import PatientHistoryItem from './PatientHistoryItem';
import AnnulRecordDialog from './AnnulRecordDialog';
import CreateReplacementRecordDialog from './CreateReplacementRecordDialog';
import ModuleCard from '../moduleCard/ModuleCard';
import ModuleEmptyState from '../moduleCard/ModuleEmptyState';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import { Button } from '@mui/material';
import ColorPalette from '../../../../colorpalette/ColorPalette';
import { DataProviderInstance } from "../../../../api/DataProvider";
import ModulePdfButton from '../moduleCard/ModulePdfButton';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import ModuleReportTemplate from '../../../../components/documents-templates/report/ModuleReportTemplate';
import { MODULE_KEYS } from '../../modules/appointmentModules';
import { REPORT_SECTIONS, buildEvolutionJob } from '../../modules/reportSections';

// Quantos registros aparecem antes do "Mostrar todas"
const PREVIEW_COUNT = 5;

const SECTION = REPORT_SECTIONS[MODULE_KEYS.EVOLUTION];

/**
 * `patientName`, `professionalId`, `companyConfig`: identificação no PDF da
 * evolução (registros não anulados, mesmo miolo do relatório completo).
 */
const PatientHistory = ({ today, data, enabled, onAssesmentChange, onEvolutionChange, collapsible = false, patientId, onRecordUpdated, patientName, professionalId, companyConfig }) => {
  const [annulDialogOpen, setAnnulDialogOpen] = useState(false);
  const [replacementDialogOpen, setReplacementDialogOpen] = useState(false);
  const [annulReason, setAnnulReason] = useState('');
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [replacementAssessment, setReplacementAssessment] = useState('');
  const [replacementEvolution, setReplacementEvolution] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const { AnnulPatientHistoryRecord, CreateReplacementHistoryRecord } = DataProviderInstance();

  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig,
    reportType: SECTION.reportType,
    prepare: () => buildEvolutionJob(data),
  });

  function onAssesmentChangeHandler(value) {
    if (onAssesmentChange) {
      onAssesmentChange(value);
    }
  }

  function onEvolutionChangeHandler(value) {
    if (onEvolutionChange) {
      onEvolutionChange(value);
    }
  }

  const handleAnnulRecord = (recordId) => {
    setSelectedRecordId(recordId);
    setAnnulReason('');
    setAnnulDialogOpen(true);
  };

  const handleCreateReplacement = (recordId, assessment, evolution) => {
    setSelectedRecordId(recordId);
    setReplacementAssessment(assessment);
    setReplacementEvolution(evolution);
    setReplacementDialogOpen(true);
  };

  const handleAnnulDialogClose = () => {
    setAnnulDialogOpen(false);
    setSelectedRecordId(null);
    setAnnulReason('');
  };

  const handleReplacementDialogClose = () => {
    setReplacementDialogOpen(false);
    setSelectedRecordId(null);
    setReplacementAssessment('');
    setReplacementEvolution('');
  };

  const submitAnnulRecord = (reason) => {
    if (!selectedRecordId || !patientId) return;

    setIsSubmitting(true);

    AnnulPatientHistoryRecord(selectedRecordId, patientId, reason, (response) => {
      setIsSubmitting(false);

      if (response && response.status === "SUCCESS") {
        handleAnnulDialogClose();
        if (onRecordUpdated) {
          onRecordUpdated();
        }
      }
    });
  };

  const submitCreateReplacement = (assessment, evolution) => {
    if (!selectedRecordId || !patientId) return;

    setIsSubmitting(true);

    CreateReplacementHistoryRecord(
      selectedRecordId,
      patientId,
      assessment,
      evolution,
      (response) => {
        setIsSubmitting(false);

        if (response && response.status === "SUCCESS") {
          handleReplacementDialogClose();
          if (onRecordUpdated) {
            onRecordUpdated();
          }
        }
      }
    );
  };

  // Function to find if a record has a replacement
  const findReplacementRecordId = (recordId) => {
    const replacement = data.find(item => item.replacesRecordId === recordId);
    return replacement ? replacement.id : null;
  };

  const hasMore = data.length > PREVIEW_COUNT;
  const visibleRecords = showAll || !hasMore ? data : data.slice(0, PREVIEW_COUNT);

  return (
    <>
      <ModuleCard
        accent="blue"
        title="Evolução do Paciente"
        icon={<AssignmentOutlinedIcon />}
        description={`Histórico de evoluções registradas (${data.length})`}
        actions={data.length > 0 && <ModulePdfButton report={report} />}
        collapsible={collapsible}
      >
        {data.length == 0 ? (
          <ModuleEmptyState icon={<AssignmentOutlinedIcon />} text="Não existem evoluções registradas." />
        ) : (
          <div className={styles.list}>
            {visibleRecords.map((item) => (
              <PatientHistoryItem
                key={item.id}
                id={item.id}
                date={item.createdAt}
                assessment={item.assessment}
                evolution={item.evolution}
                isAnnulled={item.isAnnulled === "1" || item.isAnnulled === 1}
                annulledBy={item.annulledBy}
                annulledAt={item.annulledAt}
                annulReason={item.annulReason}
                replacesRecordId={item.replacesRecordId}
                replacementRecordId={findReplacementRecordId(item.id)}
                onAnnul={handleAnnulRecord}
                onCreateReplacement={handleCreateReplacement}
                createdBy={item.createdByName}
              />
            ))}

            {hasMore && (
              <Button
                variant="text"
                size="small"
                onClick={() => setShowAll(!showAll)}
                startIcon={showAll ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
                sx={{ alignSelf: 'center', color: ColorPalette.blueMain, fontSize: '13px', fontWeight: 600, textTransform: 'none' }}
              >
                {showAll ? 'Mostrar menos' : `Mostrar todas (${data.length})`}
              </Button>
            )}
          </div>
        )}
      </ModuleCard>

      {report.job && (
        <HiddenReport>
          <ModuleReportTemplate ref={report.ref} section={SECTION} job={report.job} {...report.paperProps} />
        </HiddenReport>
      )}

      {/* Annul Record Dialog */}
      <AnnulRecordDialog
        open={annulDialogOpen}
        onClose={handleAnnulDialogClose}
        onConfirm={submitAnnulRecord}
        isSubmitting={isSubmitting}
      />

      {/* Create Replacement Record Dialog */}
      <CreateReplacementRecordDialog
        open={replacementDialogOpen}
        onClose={handleReplacementDialogClose}
        onConfirm={submitCreateReplacement}
        isSubmitting={isSubmitting}
        initialAssessment={replacementAssessment}
        initialEvolution={replacementEvolution}
      />
    </>
  );
};

export default PatientHistory;
